export type HistoricalSuggestion = { field: string; operation: string; value: string };
export type HistoricalMessage = { id: string; proposal?: { suggestions: HistoricalSuggestion[] } };
export type HistoricalAction = {
  messageId: string;
  suggestionIndex: number;
  action: "applied" | "discarded";
  proposalValue: string | null;
  createdAt: string;
};
export type ProposalDisplayState = "current" | "superseded" | "saved" | "discarded" | "pending";

function currentText(content: Record<string, unknown> | undefined, field: string): string | null {
  if (!content) return null;
  const knowledge = content.knowledge && typeof content.knowledge === "object"
    ? content.knowledge as Record<string, unknown>
    : undefined;
  const value = knowledge?.[field] ?? content[field];
  if (field === "contactChannels" && Array.isArray(value)) return JSON.stringify(value.map(item => ({ label: item.label, value: item.value })));
  return typeof value === "string" ? value.trim() : null;
}

function equalText(left: string, right: string) {
  return left.replace(/\s+/g, " ").trim() === right.replace(/\s+/g, " ").trim();
}

/**
 * An action is historical evidence. It becomes visually superseded only when
 * this session has a later applied value for the same field. The newest scalar
 * action is marked current only when it equals the canonical draft.
 */
export function proposalDisplayStates(
  messages: HistoricalMessage[],
  actions: HistoricalAction[],
  draft?: Record<string, unknown>,
): ReadonlyMap<string, ProposalDisplayState> {
  const fieldByKey = new Map<string, string>();
  const valueByKey = new Map<string, string>();
  for (const message of messages) {
    message.proposal?.suggestions.forEach((suggestion, index) => {
      fieldByKey.set(`${message.id}:${index}`, suggestion.field);
      valueByKey.set(`${message.id}:${index}`, suggestion.value);
    });
  }
  const identity = (field: string, key: string, action: HistoricalAction) => {
    if (field !== "offers") return field;
    try {
      const value: unknown = JSON.parse(action.proposalValue ?? valueByKey.get(key) ?? "");
      if (value && typeof value === "object" && "name" in value && typeof value.name === "string" && value.name.trim()) return `offers:${value.name.trim().toLocaleLowerCase("pt-BR")}`;
    } catch { /* An unknown item identity must never supersede another item. */ }
    return `offers:${key}`;
  };
  const latestApplied = new Map<string, HistoricalAction>();
  for (const action of actions) {
    if (action.action !== "applied") continue;
    const field = fieldByKey.get(`${action.messageId}:${action.suggestionIndex}`);
    if (!field) continue;
    const entity = identity(field, `${action.messageId}:${action.suggestionIndex}`, action);
    const previous = latestApplied.get(entity);
    if (!previous || new Date(action.createdAt).getTime() > new Date(previous.createdAt).getTime()) latestApplied.set(entity, action);
  }
  const states = new Map<string, ProposalDisplayState>();
  for (const action of actions) {
    const key = `${action.messageId}:${action.suggestionIndex}`;
    if (action.action === "discarded") { states.set(key, "discarded"); continue; }
    const field = fieldByKey.get(key);
    const latest = field ? latestApplied.get(identity(field, key, action)) : undefined;
    if (latest && latest !== action) { states.set(key, "superseded"); continue; }
    const canonical = field ? currentText(draft, field) : null;
    states.set(key, canonical !== null && action.proposalValue !== null && equalText(canonical, action.proposalValue) ? "current" : "saved");
  }
  return states;
}
